// src/features/chat/components/his/StarterSuggestions.jsx
/*
 * Starters — what you might want next. They live in the composer zone,
 * visible only when the composer is empty and the assistant is idle, and
 * they yield instantly to typing and assistant turns.
 *
 * Stability rule: the displayed set never changes while in view. It refreshes
 * only at natural seams — when the block reappears (after a send, after
 * typing clears, on mount), when the user returns to the surface
 * (visibility/focus), or when a tap is rejected as unavailable (that
 * rejection proves the frozen set is stale; a transient callable failure is
 * not such proof and leaves the set in place — see onSelect's return
 * contract). Predictions must not churn under a reaching finger; tap-time
 * preflight still enforces expiry.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isPrimitiveActive } from '../../models/interactionPrimitiveModel';
import styles from './StarterSuggestions.module.css';

const MAX_STARTERS = 3;

export function StarterSuggestions({
  primitives = [],
  composerEmpty = true,
  assistantIdle = true,
  disabled = false,
  onSelect,
}) {
  const [displayed, setDisplayed] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const primitivesRef = useRef(primitives);
  primitivesRef.current = primitives;

  const shouldShow = composerEmpty && assistantIdle;
  const wasShownRef = useRef(false);

  // Every adoption filters locally expired docs: the live list's expiry
  // filter runs against a stale clock, so seams must not re-adopt dead chips.
  const adoptDisplayed = useCallback(() => {
    setDisplayed(
      primitivesRef.current
        .filter((primitive) => isPrimitiveActive(primitive))
        .slice(0, MAX_STARTERS)
    );
  }, []);

  // Seam: the block transitions hidden → shown, or it is shown but empty.
  useEffect(() => {
    if (!shouldShow) {
      wasShownRef.current = false;
      return;
    }
    if (!wasShownRef.current || displayed.length === 0) {
      wasShownRef.current = true;
      adoptDisplayed();
    }
  }, [shouldShow, displayed.length, primitives, adoptDisplayed]);

  // Seam: the user returns to the surface.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      adoptDisplayed();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [adoptDisplayed]);

  if (!shouldShow || displayed.length === 0) return null;

  const handleSelect = async (primitive) => {
    if (!onSelect || busyId) return;
    setBusyId(primitive.id);
    try {
      const ok = await onSelect(primitive);
      // Seam: a rejected tap proves the frozen set is stale.
      if (ok === false) {
        adoptDisplayed();
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.zone} role="group" aria-label="Suggestions">
      {/* One quiet line that slides sideways when needed. Ellipsized labels
          are safe: a tap seeds the full text into the composer for review
          before send — never a blind commit. */}
      <div className={styles.strip}>
        {displayed.map((primitive) => (
          <button
            key={primitive.id}
            type="button"
            className={styles.chip}
            data-busy={busyId === primitive.id ? 'true' : 'false'}
            onClick={() => handleSelect(primitive)}
            disabled={disabled || Boolean(busyId)}
            title={primitive.userMessageText}
          >
            {primitive.userMessageText}
          </button>
        ))}
      </div>
    </div>
  );
}

export default StarterSuggestions;
